import axios from "axios"

// Pre-auth endpoints: aucun Sanctum token requis, le lead n'a pas encore de compte.
// Miroir de summerFunnelService sur le contrat /v1/stage-funnel/* (catalogue-driven).

// Map frontend camelCase state keys → snake_case wire format expected by Laravel.
const camelToSnakeAnswers = (data) => {
    if (!data) return data
    const mapping = {
        studentClass: "student_class",
        studentClassRentree: "student_class_rentree",
        niveauActuel: "niveau_actuel",
        besoinsSpeciaux: "besoins_speciaux",
        // Composition par sessions (contrat v2) : `sessions` = dates ISO des
        // sessions cochées (clé déjà snake-compatible), `sessionSubjects` =
        // matières PAR session ({ "2026-08-03": [ids…] }).
        sessionSubjects: "session_subjects",
        // Volume horaire voulu par matière et par session — c'est lui qui
        // répartit les chapitres côté composition.
        sessionSubjectHours: "session_subject_hours",
        // L'objectif visé par matière et par session — même forme que les
        // volumes ci-dessus, valeurs = crans de l'échelle (declic → expert).
        sessionSubjectGoals: "session_subject_goals",
        // L'issue de secours du compositeur : la famille garde le programme
        // proposé et décidera des chapitres plus tard avec l'élève.
        programDecidedLater: "program_decided_later",
        // « Modifier le programme » : la phrase du parent. Une chaîne vide
        // efface la modification (retour au programme proposé).
        programInstructions: "program_instructions",
        slotKey: "slot_key",
        templateSlug: "template_slug",
        leadIntent: "lead_intent",
        parentEmail: "parent_email",
    }
    return Object.fromEntries(
        Object.entries(data)
            // Drop undefined/null values so the backend's `sometimes` rules
            // treat them as not sent (partiels OK sur /answers).
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([k, v]) => [mapping[k] || k, normalizeWireValue(k, v)])
    )
}

// Les chips manipulent des ids de matières en STRING ; le wire les porte en
// int (le backend valide des ids numériques). Une seule couture, ici.
const normalizeWireValue = (key, value) => {
    if (key === "sessionSubjects" && value && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value).map(([date, ids]) => [
                date,
                (Array.isArray(ids) ? ids : []).map((id) => Number(id)),
            ])
        )
    }
    if (key === "sessionSubjectHours" && value && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value).map(([sessionKey, hours]) => [
                sessionKey,
                Object.fromEntries(
                    Object.entries(hours || {}).map(([id, h]) => [
                        Number(id),
                        Number(h),
                    ])
                ),
            ])
        )
    }
    if (key === "sessionSubjectGoals" && value && typeof value === "object") {
        // Ids en int comme partout sur le wire ; le cran reste une chaîne, qui
        // EST le contrat avec le backend (jamais traduite).
        return Object.fromEntries(
            Object.entries(value).map(([sessionKey, goals]) => [
                sessionKey,
                Object.fromEntries(
                    Object.entries(goals || {}).map(([id, slug]) => [
                        Number(id),
                        slug,
                    ])
                ),
            ])
        )
    }
    if (key === "subjects" && Array.isArray(value)) {
        return value.map((id) => Number(id))
    }
    return value
}

/**
 * Hydrate le funnel Stage depuis un submissionGUID HubSpot (redirect LP via
 * /register/fast-track). Le backend find-or-create le lead, pose l'état
 * `stage_started` et résout le template depuis `lead_intent`. On passe aussi le
 * lead_intent vu au dispatch (redondant mais utile au backend pour résoudre sans
 * re-fetch HubSpot). Renvoie funnel_state_id + user pré-rempli (+ template).
 */
function initStageFromHubSpot({}, data) {
    return axios.post(`/api/v1/stage-funnel/init`, camelToSnakeAnswers(data))
}

// Entrée directe (sans GUID) : firstname, student_class, email, template_slug.
function startStageFunnel({}, data) {
    return axios.post(`/api/v1/stage-funnel/start`, camelToSnakeAnswers(data))
}

// Persiste classe/rentrée, objectif, niveau, besoins_speciaux[], sessions[]
// (dates ISO cochées), session_subjects{}, slot_key (partiels OK) —
// auto-transition `stage_diag_completed` côté backend dès le diagnostic complet.
function updateStageAnswers({ stateId }, data) {
    return axios.put(
        `/api/v1/stage-funnel/${stateId}/answers`,
        camelToSnakeAnswers(data)
    )
}

// Stripe embedded checkout 1 € (`pre_inscription_1eur_ht`) — la sélection
// (sessions cochées / matières par session / horaire) est validée contre le
// catalogue vivant puis encodée en metadata côté backend.
function createStageCheckoutSession({ stateId }, data) {
    return axios.post(
        `/api/v1/stage-funnel/${stateId}/create-checkout-session`,
        camelToSnakeAnswers(data)
    )
}

/**
 * Resume payload + config catalogue : template (nom, labels), sessions
 * proposées [{key, start_date, end_date, label}], pricing_by_count
 * {"1": {variant_key, price_cents, remainder_after_preinscription_cents}, …},
 * max_subjects_per_session, horaires [{slot_key, séances}], FAQ (+ variantes
 * en compat). Source UNIQUE des prix / sessions / horaires côté front.
 */
function getStageFunnelState({ stateId }) {
    return axios.get(`/api/v1/stage-funnel/${stateId}`)
}

/**
 * Matières proposables — chapitres validés pour la classe ∩ scope M2M du
 * template. Réponse : { data: [{ id, name, slug, icon_path, color, rank }] }.
 */
function getStageSubjects({ studentClass, templateSlug }) {
    return axios.get(`/api/v1/stage-funnel/subjects`, {
        params: {
            student_class: studentClass,
            ...(templateSlug ? { template_slug: templateSlug } : {}),
        },
    })
}

/**
 * Chapitres proposables d'une matière pour une classe — ce dans quoi la modale
 * de retouche du programme fait piocher.
 * Réponse : { data: { chapters: [{ id, name, theme, class }] } }.
 */
function getStageChapters({ studentClass, schoolSubjectId }) {
    return axios.get(`/api/v1/stage-funnel/chapters`, {
        params: {
            student_class: studentClass,
            school_subject_id: schoolSubjectId,
        },
    })
}

/**
 * Retouche des chapitres d'UNE séance du programme composé — la nouveauté
 * produit des formules vendues au créneau. On envoie la liste VOULUE, pas un
 * delta : rejouable sans effet de bord.
 */
function updateStageProgramSeance({ stateId }, data) {
    return axios.put(`/api/v1/stage-funnel/${stateId}/program/seance`, {
        study_program_id: data.studyProgramId,
        day: data.day,
        chapter_ids: data.chapterIds,
    })
}

/**
 * Programme personnalisé (reveal) : calendrier de chapitres composé sur les
 * SEMAINES COCHÉES avec leurs matières (2 slots de chapitre par jour — séances
 * de 2h, ~1h/chapitre). Même shape de réponse que le programme été (consommée
 * par SummerProgramCalendar).
 */
function getStageProgram({ stateId }, data) {
    return axios.post(
        `/api/v1/stage-funnel/${stateId}/program`,
        camelToSnakeAnswers(data)
    )
}

/**
 * Tuteurs ("masteurs") qui correspondent au profil de l'enfant — étape "aha
 * moment" avant le checkout. Même shape que le matching été : { data: { count,
 * total, showcase: [...], more_avatars: [...] } }. Étape NON bloquante : en cas
 * d'erreur/vide, l'écran retombe sur un repli gracieux (cf. StageTutorsScreen).
 */
function getStageMatchingTutors({ stateId }, data) {
    return axios.post(
        `/api/v1/stage-funnel/${stateId}/matching-tutors`,
        camelToSnakeAnswers(data)
    )
}

/**
 * Reprise d'un funnel Stage depuis un lien magique émis par le BO :
 * GET /stage/resume?user=<base64email> derrière `auth_or_email`. Renvoie le
 * dernier stage FastTrackState pour hydrater le Context et reset vers la bonne
 * étape. Nommé `resumeStage` (et non `resume`) car le reducer api indexe le
 * state par nom de fonction — un homonyme entrerait en collision avec les
 * `resume`/`resumeSummer` des autres funnels.
 */
function resumeStage({}, {}, queryParams) {
    return axios.get(`/api/v1/stage-funnel/resume`, { params: queryParams })
}

export const stageFunnelService = {
    initStageFromHubSpot,
    startStageFunnel,
    updateStageAnswers,
    createStageCheckoutSession,
    getStageFunnelState,
    getStageSubjects,
    getStageChapters,
    getStageProgram,
    updateStageProgramSeance,
    getStageMatchingTutors,
    resumeStage,
}
